import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from "react";
import {
  Archive,
  ArrowDownToLine,
  ArrowRight,
  Braces,
  BookOpen,
  Check,
  ClipboardCopy,
  Copy,
  Download,
  ExternalLink,
  FileCode2,
  FileText,
  Images,
  Languages,
  Link2,
  LockKeyhole,
  Layers3,
  Mail,
  MessageCircle,
  Quote,
  RefreshCw,
  Target,
  Table2,
  ThumbsDown,
  ThumbsUp,
  Video,
  Wrench
} from "lucide-react";
import { resolvePost, ResolveError } from "./api";
import { track, trackAIReferralLanding } from "./analytics";
import { embedAvatarImages } from "./avatarEmbed";
import { getPublicConfig } from "./config";
import { fetchMediaForDownload } from "./download";
import { getHomeCopy } from "./homeI18n";
import { HomePage, HomePlatformNav } from "./HomePage";
import { allGuideContent, getGuideHub, getGuidePage, getGuideTitle, isGuideId, type GuideId } from "./guides";
import { getYouTubeGuide, isYouTubeGuideId, isYouTubeGuideLocale, youtubeGuideId, youtubeGuideIds } from "./youtubeGuide";
import { getCopy, threadsLoginRequired, translations, type Copy as CopyType, type Locale } from "./i18n";
import { legalPage } from "./legal";
import { completeLocalesForPage, intentPlatform, isIntentPageKind, localizedPath, parseRoute, type PageKind } from "./route";
import { GrabSiloDesktopPage } from "./GrabSiloDesktopPage";
import { getGrabSiloDesktopCopy, grabSiloDesktopLocale } from "./grabSiloDesktopI18n";
import { buildThreadHtml } from "./threadHtml";
import { buildThreadCsv } from "./threadCsv";
import { buildThreadText } from "./threadText";
import { buildProfileCsv } from "./profileCsv";
import { buildProfileHtml } from "./profileHtml";
import { buildProfileText } from "./profileText";
import { beginPlatformHandoff, platformDownloaderPath, usePlatformHandoff } from "./platformHandoff";
import { detectPlatform, isSupportedPlatformURL, platformLabel, threadsTargetType, type PlatformID } from "./platforms";
import { ThreadsCompatibilityPage, ThreadsEvidencePreview } from "./ThreadsCompatibilityPage";
import { getThreadsCompatibilityContent, isThreadsCompatibilityLocale } from "./threadsCompatibility";
import type { Author, MediaItem, Reply, ResolveResponse } from "./types";
import { buildMediaZip, mediaZipBatches, saveBlob, ZipError } from "./zip";
import { DesktopPromoStrip } from "./DesktopPromoStrip";

export interface AppProps {
  url: string;
  publicConfig?: ReturnType<typeof getPublicConfig>;
  routePage?: ReactNode;
  footerNote?: string | null;
}

export function shouldTrackAIReferralLanding(page: PageKind): boolean {
  return page !== "not-found";
}

export default function App({ url, publicConfig = getPublicConfig(), routePage, footerNote }: AppProps) {
  const route = parseRoute(url);
  const copy = getCopy(route.locale);
  const routeIntentPlatform = isIntentPageKind(route.page) ? intentPlatform(route.page) : null;
  const page: PageKind = (route.page === "tiktok-downloader" && !publicConfig.tiktokEnabled) || (route.page === "douyin-downloader" && !publicConfig.douyinEnabled) || ((route.page === "x-downloader" || routeIntentPlatform === "x") && !publicConfig.xEnabled) || (route.page === "bilibili-downloader" && !publicConfig.bilibiliEnabled) ? "not-found" : route.page;
  const downloaderPage = page === "downloader" || page === "instagram-downloader" || page === "youtube-downloader" || page === "facebook-downloader" || page === "tiktok-downloader" || page === "douyin-downloader" || page === "x-downloader" || page === "bilibili-downloader" || isIntentPageKind(page);
  const usesRoutePage = page === "instagram-downloader" || page === "youtube-downloader" || page === "facebook-downloader" || page === "tiktok-downloader" || page === "douyin-downloader" || page === "x-downloader" || page === "bilibili-downloader" || isIntentPageKind(page) || isYouTubeGuideId(page);
  const landingTracked = useRef(false);

  useEffect(() => {
    if (landingTracked.current) return;
    landingTracked.current = true;
    if (shouldTrackAIReferralLanding(page)) trackAIReferralLanding(page);
  }, [page]);

  return (
    <div className={`site-shell${downloaderPage ? " downloader-shell" : ""}`}>
      <Header locale={route.locale} page={page} copy={copy} tiktokEnabled={publicConfig.tiktokEnabled === true} douyinEnabled={publicConfig.douyinEnabled === true} xEnabled={publicConfig.xEnabled === true} bilibiliEnabled={publicConfig.bilibiliEnabled === true} />
      {page === "home" ? (
        <HomePage locale={route.locale} flags={{ tiktokEnabled: publicConfig.tiktokEnabled === true, douyinEnabled: publicConfig.douyinEnabled === true, xEnabled: publicConfig.xEnabled === true, bilibiliEnabled: publicConfig.bilibiliEnabled === true }} />
      ) : page === "desktop" ? (
        <GrabSiloDesktopPage locale={route.locale} macArm64Url={publicConfig.desktopMacArm64Url} macX64Url={publicConfig.desktopMacX64Url} />
      ) : page === "downloader" ? (
        <DownloaderPage locale={route.locale} copy={copy} adsEnabled={publicConfig.adsEnabled} />
      ) : page === "threads-downloader-compatibility" ? (
        <ThreadsCompatibilityPage locale={route.locale} />
      ) : usesRoutePage ? (
        routePage || <NotFound locale={route.locale} copy={copy} />
      ) : page === "guides" ? (
        <GuideHubPage locale={route.locale} />
      ) : isGuideId(page) ? (
        <GuidePage locale={route.locale} id={page} />
      ) : page === "not-found" ? (
        <NotFound locale={route.locale} copy={copy} />
      ) : (
        <LegalPage locale={route.locale} page={page} copy={copy} />
      )}
      <Footer locale={route.locale} copy={copy} tiktokEnabled={publicConfig.tiktokEnabled} douyinEnabled={publicConfig.douyinEnabled} xEnabled={publicConfig.xEnabled} bilibiliEnabled={publicConfig.bilibiliEnabled} footerNote={page === "desktop" ? null : page === "home" ? getHomeCopy(route.locale).footerNote : footerNote} />
    </div>
  );
}

const guideNavLabels: Record<Locale, string> = {
  en: "Guides", "zh-tw": "指南", "zh-cn": "指南", ja: "ガイド", ko: "가이드", es: "Guías", "pt-br": "Guias", fr: "Guides", de: "Leitfäden", id: "Panduan", vi: "Hướng dẫn", th: "คู่มือ"
};

const footerYouTubeIntentLinks: Partial<Record<Locale, readonly { page: Exclude<PageKind, "not-found">; label: string }[]>> = {
  en: [
    { page: "youtube-to-mp4", label: "YouTube MP4" },
    { page: "youtube-to-mp3", label: "YouTube MP3" },
    { page: "youtube-shorts-downloader", label: "Shorts" },
    { page: "youtube-4k-downloader", label: "YouTube 4K" },
    { page: "download-youtube-subtitles", label: "Subtitles" }
  ],
  "zh-tw": [
    { page: "youtube-to-mp4", label: "YouTube MP4" },
    { page: "youtube-to-mp3", label: "YouTube MP3" },
    { page: "youtube-shorts-downloader", label: "Shorts 下載" },
    { page: "youtube-4k-downloader", label: "YouTube 4K" },
    { page: "download-youtube-subtitles", label: "YouTube 字幕" }
  ],
  ja: [
    { page: "youtube-to-mp4", label: "YouTube MP4" },
    { page: "youtube-shorts-downloader", label: "Shorts保存" }
  ],
  id: [
    { page: "youtube-to-mp4", label: "YouTube MP4" },
    { page: "youtube-shorts-downloader", label: "Unduh Shorts" }
  ]
};

function Header({ locale, page, copy, tiktokEnabled, douyinEnabled, xEnabled, bilibiliEnabled }: { locale: Locale; page: PageKind; copy: CopyType; tiktokEnabled: boolean; douyinEnabled: boolean; xEnabled: boolean; bilibiliEnabled: boolean }) {
  const currentIntentPlatform = isIntentPageKind(page) ? intentPlatform(page) : null;
  const isYouTubeContext = page === "youtube-downloader" || currentIntentPlatform === "youtube" || isYouTubeGuideId(page);
  const downloader = page === "instagram-downloader" || currentIntentPlatform === "instagram" ? localizedPath(locale, "instagram-downloader") : page === "facebook-downloader" || currentIntentPlatform === "facebook" ? localizedPath(locale, "facebook-downloader") : page === "tiktok-downloader" ? localizedPath(locale, "tiktok-downloader") : page === "douyin-downloader" ? localizedPath(locale, "douyin-downloader") : page === "x-downloader" || currentIntentPlatform === "x" ? localizedPath(locale, "x-downloader") : page === "bilibili-downloader" ? localizedPath(locale, "bilibili-downloader") : isYouTubeContext ? localizedPath(locale, "youtube-downloader") : localizedPath(locale, "downloader");
  const anchor = (id: string) => page === "downloader" || page === "instagram-downloader" || page === "youtube-downloader" || page === "facebook-downloader" || page === "tiktok-downloader" || page === "douyin-downloader" || page === "x-downloader" || page === "bilibili-downloader" || isIntentPageKind(page) ? `#${id}` : `${downloader}#${id}`;
  const localizedPage = page === "not-found" ? "downloader" : page;
  const availableLocales = completeLocalesForPage(page);
  const guideDestination = isYouTubeContext && isYouTubeGuideLocale(youtubeGuideId, locale)
    ? localizedPath(locale, youtubeGuideId)
    : localizedPath(locale, "guides");
  const homeFlags = { tiktokEnabled, douyinEnabled, xEnabled, bilibiliEnabled };
  const isDownloaderPage = page === "downloader" || page === "instagram-downloader" || page === "youtube-downloader" || page === "facebook-downloader" || page === "tiktok-downloader" || page === "douyin-downloader" || page === "x-downloader" || page === "bilibili-downloader" || isIntentPageKind(page);
  const isDesktopProduct = page === "desktop";
  const desktopCopy = getGrabSiloDesktopCopy(locale);
  const activePlatform: PlatformID | undefined = page === "instagram-downloader" ? "instagram" : page === "youtube-downloader" ? "youtube" : page === "facebook-downloader" ? "facebook" : page === "tiktok-downloader" ? "tiktok" : page === "douyin-downloader" ? "douyin" : page === "x-downloader" ? "x" : page === "bilibili-downloader" ? "bilibili" : page === "downloader" ? "threads" : currentIntentPlatform || undefined;
  return (
    <header className={`topbar${page === "home" || isDownloaderPage || isDesktopProduct ? " home-topbar" : ""}`}>
      <a className="brand" href={localizedPath(locale, "home")} aria-label="SiloSave home">
        <span className="brand-mark"><Archive size={18} strokeWidth={2.4} /></span>
        <span>SiloSave</span>
      </a>
      {page === "home" || isDownloaderPage ? (
        <HomePlatformNav locale={locale} flags={homeFlags} active={activePlatform} />
      ) : null}
      <nav className="nav-links" aria-label="Primary navigation">
        {page !== "home" && !isDownloaderPage && !isDesktopProduct && <a href={anchor("how")}>{copy.navHow}</a>}
        <a className={isDesktopProduct ? "active" : undefined} href={localizedPath(grabSiloDesktopLocale(locale), "desktop")}>{desktopCopy.navLabel}</a>
        <a href={guideDestination}>{guideNavLabels[locale]}</a>
        <a href={isDesktopProduct ? "#desktop-faq" : page === "home" ? "#faq" : anchor("faq")}>{copy.navFaq}</a>
      </nav>
      <label className="language-select">
        <Languages size={17} aria-hidden="true" />
        <span className="sr-only">Language</span>
        <select
          aria-label="Language"
          value={locale}
          onChange={(event) => {
            window.location.href = localizedPath(event.target.value as Locale, localizedPage);
          }}
        >
          {availableLocales.map((item) => <option key={item} value={item}>{translations[item].languageName}</option>)}
        </select>
      </label>
    </header>
  );
}

function GuideHubPage({ locale }: { locale: Locale }) {
  const hub = getGuideHub(locale);
  const guides = allGuideContent(locale);
  return (
    <main className="guide-main">
      <section className="guide-hero" aria-labelledby="guide-hub-title">
        <div>
          <p className="section-kicker">SiloSave / {guideNavLabels[locale]}</p>
          <h1 id="guide-hub-title">{hub.title}</h1>
          <p className="guide-lead">{hub.lead}</p>
          <EditorialMeta publishedLabel={hub.publishedLabel} updatedLabel={hub.updatedLabel} publishedAt={hub.publishedAt} updatedAt={hub.updatedAt} byline={hub.byline} />
        </div>
        <GuideVisual id="guides" title={hub.visualAlt} />
      </section>

      <section className="guide-answer" aria-labelledby="guide-hub-answer">
        <p className="section-kicker">{hub.answerKicker}</p>
        <h2 id="guide-hub-answer">{hub.answerHeading}</h2>
        <p>{hub.answer}</p>
      </section>

      <GuideScreenshot locale={locale} alt={hub.screenshotAlt} />

      <section className="guide-index" aria-labelledby="guide-list-title">
        <div className="section-heading compact-heading"><h2 id="guide-list-title">{hub.cardsHeading}</h2></div>
        <div className="guide-card-grid">
          {guides.map((guide, index) => (
            <article className="guide-card" key={guide.id}>
              <span>0{index + 1}</span>
              <GuideIcon id={guide.id} />
              <h3>{guide.title}</h3>
              <p>{guide.focus}</p>
              <a href={localizedPath(locale, guide.id)}>{hub.readLabel}<ArrowRight size={16} /></a>
            </article>
          ))}
        </div>
      </section>

      {locale === "en" && <section className="guide-section related-guides" aria-labelledby="youtube-guide-list-title">
        <h2 id="youtube-guide-list-title">YouTube download guides</h2>
        <div>{youtubeGuideIds.map((id) => {
          const guide = getYouTubeGuide("en", id);
          return <a key={id} href={localizedPath("en", id)}><span><strong>{guide.title}</strong><small>{guide.description}</small></span><ArrowRight size={15} /></a>;
        })}</div>
      </section>}

      <GuideCTA locale={locale} guideId="guides" title={hub.ctaTitle} text={hub.ctaText} label={hub.ctaLabel} />
      <p className="editorial-method">{hub.editorialMethod}</p>
    </main>
  );
}

function GuidePage({ locale, id }: { locale: Locale; id: GuideId }) {
  const guide = getGuidePage(locale, id);
  return (
    <main className="guide-main">
      <nav className="breadcrumbs" aria-label="Breadcrumb">
        <a href={localizedPath(locale, "home")}>SiloSave</a><span>/</span><a href={localizedPath(locale, "guides")}>{guideNavLabels[locale]}</a><span>/</span><span>{guide.title}</span>
      </nav>
      <article>
        <header className="guide-hero">
          <div>
            <p className="section-kicker">SiloSave / {guideNavLabels[locale]}</p>
            <h1>{guide.title}</h1>
            <p className="guide-lead">{guide.summary}</p>
            <EditorialMeta publishedLabel={guide.publishedLabel} updatedLabel={guide.updatedLabel} publishedAt={guide.publishedAt} updatedAt={guide.updatedAt} byline={guide.byline} />
          </div>
          <GuideVisual id={guide.id} title={guide.visualAlt} />
        </header>

        <section className="guide-answer" aria-labelledby="direct-answer-title">
          <p className="section-kicker">{guide.answerKicker}</p>
          <h2 id="direct-answer-title">{guide.directAnswerHeading}</h2>
          <p>{guide.directAnswer}</p>
        </section>

        <section className="guide-section" aria-labelledby="focus-title">
          <h2 id="focus-title">{guide.focusHeading}</h2>
          <p>{guide.focus}</p>
        </section>

        <section className="guide-section" aria-labelledby="steps-title">
          <h2 id="steps-title">{guide.stepsHeading}</h2>
          <ol className="guide-steps">
            {guide.steps.map((step, index) => <li id={`step-${index + 1}`} key={step.title}><span>0{index + 1}</span><div><h3>{step.title}</h3><p>{step.text}</p></div></li>)}
          </ol>
        </section>

        <GuideScreenshot locale={locale} alt={guide.screenshotAlt} />

        <section className="guide-section" aria-labelledby="facts-title">
          <h2 id="facts-title">{guide.factsHeading}</h2>
          <div className="guide-table-wrap"><table><thead><tr><th>{guide.factColumns[0]}</th><th>{guide.factColumns[1]}</th></tr></thead><tbody>{guide.facts.map((fact) => <tr key={fact.label}><th scope="row">{fact.label}</th><td>{fact.value}</td></tr>)}</tbody></table></div>
        </section>

        <section className="guide-section guide-limits" aria-labelledby="limits-title">
          <h2 id="limits-title">{guide.limitsHeading}</h2>
          <ul>{guide.limits.map((limit) => <li key={limit}>{limit}</li>)}</ul>
        </section>

        <section className="guide-section" aria-labelledby="guide-faq-title">
          <h2 id="guide-faq-title">{guide.faqHeading}</h2>
          <div className="faq-list">{guide.faqs.map((item) => <details key={item.question}><summary>{item.question}</summary><p>{item.answer}</p></details>)}</div>
        </section>

        <section className="guide-section source-list" aria-labelledby="sources-title">
          <h2 id="sources-title">{guide.sourcesHeading}</h2>
          <ul>{guide.sources.map((source) => <li key={source.url}><a href={source.url} target="_blank" rel="noreferrer">{source.title}</a><span>{source.publisher}</span></li>)}</ul>
          <p className="editorial-method">{guide.editorialMethod}</p>
        </section>

        <section className="guide-section related-guides" aria-labelledby="related-title">
          <h2 id="related-title">{guide.relatedHeading}</h2>
          <div>{guide.related.map((related) => <a key={related} href={localizedPath(locale, related)}>{getGuideTitle(locale, related)}<ArrowRight size={15} /></a>)}</div>
        </section>

        <GuideCTA locale={locale} guideId={guide.id} title={guide.ctaTitle} text={guide.ctaText} label={guide.ctaLabel} />
      </article>
    </main>
  );
}

function EditorialMeta({ publishedLabel, updatedLabel, publishedAt, updatedAt, byline }: { publishedLabel: string; updatedLabel: string; publishedAt: string; updatedAt: string; byline: string }) {
  return <div className="editorial-meta"><strong>{byline}</strong><span>{publishedLabel} <time dateTime={publishedAt}>{publishedAt}</time></span><span>{updatedLabel} <time dateTime={updatedAt}>{updatedAt}</time></span></div>;
}

function GuideCTA({ locale, guideId, title, text, label }: { locale: Locale; guideId: GuideId | "guides"; title: string; text: string; label: string }) {
  return <aside className="guide-cta"><div><p className="section-kicker">SiloSave / Threads</p><h2>{title}</h2><p>{text}</p></div><a href={localizedPath(locale, "downloader")} onClick={() => track("guide_cta_click", { guide_id: guideId, target: "downloader" })}>{label}<ArrowRight size={17} /></a></aside>;
}

function GuideScreenshot({ locale, alt }: { locale: Locale; alt: string }) {
  return (
    <figure className="guide-screenshot">
      <picture>
        <source media="(min-width: 760px)" srcSet={`/guides/screenshots/${locale}-1200.jpg 1200w, /guides/screenshots/${locale}-640.jpg 640w`} sizes="(max-width: 1180px) calc(100vw - 40px), 1120px" />
        <img src={`/guides/screenshots/${locale}-640.jpg`} alt={alt} loading="lazy" width="640" height="440" />
      </picture>
    </figure>
  );
}

function GuideIcon({ id }: { id: GuideId }) {
  if (id.includes("video")) return <Video aria-hidden="true" />;
  if (id.includes("photo")) return <Images aria-hidden="true" />;
  if (id.includes("text")) return <FileText aria-hidden="true" />;
  if (id.includes("replies")) return <MessageCircle aria-hidden="true" />;
  if (id.includes("formats")) return <Layers3 aria-hidden="true" />;
  if (id.includes("iphone") || id.includes("android")) return <Download aria-hidden="true" />;
  if (id.includes("privacy")) return <LockKeyhole aria-hidden="true" />;
  return <Wrench aria-hidden="true" />;
}

function GuideVisual({ id, title }: { id: GuideId | "guides"; title: string }) {
  const index = id === "guides" ? 0 : Math.max(1, allGuideContent("en").findIndex((guide) => guide.id === id) + 1);
  const hue = (index * 37 + 226) % 360;
  return (
    <svg className="guide-visual" viewBox="0 0 560 360" role="img" aria-label={title}>
      <defs><linearGradient id={`guide-gradient-${index}`} x1="0" x2="1"><stop stopColor={`hsl(${hue} 76% 58%)`} /><stop offset="1" stopColor="#b8f34b" /></linearGradient></defs>
      <rect x="8" y="8" width="544" height="344" rx="34" fill="#111827" />
      <circle cx="460" cy="76" r="78" fill={`url(#guide-gradient-${index})`} opacity=".88" />
      <path d="M64 255 C150 175 205 305 300 210 S445 132 510 228" fill="none" stroke={`url(#guide-gradient-${index})`} strokeWidth="18" strokeLinecap="round" />
      <rect x="54" y="52" width="236" height="118" rx="24" fill="#fff" />
      <rect x="84" y="82" width="128" height="13" rx="7" fill="#111827" />
      <rect x="84" y="112" width="176" height="10" rx="5" fill="#9aa3b2" />
      <rect x="84" y="137" width="102" height="10" rx="5" fill="#9aa3b2" />
      <circle cx="104" cy="286" r="26" fill="#4a67ff" />
      <path d="M96 286h16M104 278v16" stroke="#fff" strokeWidth="4" strokeLinecap="round" />
      <text x="344" y="316" fill="#fff" fontSize="18" fontWeight="800" letterSpacing="2">GRABSILO / {String(index).padStart(2, "0")}</text>
    </svg>
  );
}

function DownloaderPage({ locale, copy, adsEnabled }: { locale: Locale; copy: CopyType; adsEnabled: boolean }) {
  const showsCompatibility = isThreadsCompatibilityLocale(locale);
  return (
    <main>
      <section className="hero" aria-labelledby="hero-title">
        <div className="hero-copy">
          <h1 id="hero-title">{copy.seoTitle.split(/\s*(?:[|｜—]|[:：])\s*/)[0]}</h1>
          <p className="hero-lead">{copy.heroLead}</p>
          <div className="support-strip">
            <Images size={17} />
            <Video size={17} />
            <FileText size={17} />
            <span>{copy.supportedNote}</span>
          </div>
        </div>
        <DownloaderPanel locale={locale} copy={copy} />
        <div className="hero-orbit orbit-one" aria-hidden="true" />
        <div className="hero-orbit orbit-two" aria-hidden="true" />
      </section>

      <AdSlot enabled={adsEnabled} label={copy.adLabel} />

      <section id="how" className="section process-section">
        <div className="section-heading">
          <p className="section-kicker">01 / FLOW</p>
          <h2>{copy.howTitle}</h2>
          <p>{copy.howLead}</p>
        </div>
        <div className="steps-grid">
          {copy.steps.map(([title, text], index) => (
            <article className="step-card" key={title}>
              <span className="step-number">0{index + 1}</span>
              <div className="step-icon">{index === 0 ? <Link2 /> : index === 1 ? <Target /> : <Download />}</div>
              <h3>{title}</h3>
              <p>{text}</p>
              {index < 2 && <ArrowRight className="step-arrow" aria-hidden="true" />}
            </article>
          ))}
        </div>
      </section>

      <ThreadsEvidencePreview locale={locale} />

      <section id="faq" className="section faq-section" aria-labelledby="faq-title">
        <div className="section-heading compact-heading">
          <p className="section-kicker">{showsCompatibility ? "03 / FAQ" : "02 / FAQ"}</p>
          <h2 id="faq-title">{copy.faqTitle}</h2>
        </div>
        <div className="faq-list">
          {copy.faqs.map(([question, answer]) => (
            <details key={question}>
              <summary>{question}</summary>
              <p>{answer}</p>
            </details>
          ))}
        </div>
      </section>

    </main>
  );
}

function DownloaderPanel({ locale, copy }: { locale: Locale; copy: CopyType }) {
  const [url, setURL] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ResolveResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mismatch, setMismatch] = useState<Exclude<PlatformID, "threads"> | null>(null);
  const [requestID, setRequestID] = useState<string | undefined>();
  const [desktopHint, setDesktopHint] = useState(false);
  const [zipState, setZipState] = useState<"idle" | "loading">("idle");
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => () => abortRef.current?.abort(), []);

  async function submit(event?: FormEvent, overrideURL?: string) {
    event?.preventDefault();
    const input = (overrideURL ?? url).trim();
    setMismatch(null);
    setDesktopHint(false);
    if (!input) {
      setError(copy.errors.INVALID_URL);
      return;
    }
    const platform = detectPlatform(input);
    if (platform && platform !== "threads") {
      if (!isSupportedPlatformURL(platform, input)) {
        setError(copy.errors.INVALID_URL);
        setResult(null);
        return;
      }
      if (beginPlatformHandoff(locale, platform, input)) return;
      setError(copy.errors.INVALID_URL);
      setMismatch(platform);
      setResult(null);
      setRequestID(undefined);
      return;
    }
    const targetType = threadsTargetType(input);
    if (!targetType) {
      setError(copy.errors.INVALID_URL);
      setResult(null);
      setRequestID(undefined);
      return;
    }
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError(null);
    setRequestID(undefined);
    track("resolve_started", { platform: "threads", target_type: targetType });
    try {
      const resolved = await resolvePost(input, controller.signal);
      if (resolved.platform !== "threads") throw new ResolveError("UNSUPPORTED_PLATFORM");
      setResult(resolved);
      track("resolve_result", {
        platform: "threads",
        target_type: targetType,
        content_type: resolved.contentType === "profile" ? "profile" : "post",
        status: "ok",
        media_count: resolved.media.length,
        reply_count: resolved.replies.length
      });
      requestAnimationFrame(() => {
        const resultCard = document.getElementById("result-card");
        resultCard?.focus({ preventScroll: true });
      });
    } catch (caught) {
      if (controller.signal.aborted) return;
      const resolveError = caught instanceof ResolveError ? caught : new ResolveError("NETWORK_ERROR");
      setResult(null);
      if (resolveError.code === "LOGIN_REQUIRED") {
        setError(threadsLoginRequired[locale].message);
        setDesktopHint(true);
      } else {
        setError(copy.errors[resolveError.code as keyof typeof copy.errors] || copy.errors.NETWORK_ERROR);
      }
      setRequestID(resolveError.requestId);
      track("resolve_result", { platform: "threads", target_type: targetType, status: resolveError.code });
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }

  usePlatformHandoff("threads", (input) => {
    setURL(input);
    return submit(undefined, input);
  });

  async function createZip(items: MediaItem[], filename: string): Promise<boolean> {
    if (!items.length) return false;
    setZipState("loading");
    setError(null);
    try {
      const blob = await buildMediaZip(items);
      saveBlob(blob, filename);
      return true;
    } catch (caught) {
      const zipError = caught instanceof ZipError ? caught : new ZipError("ZIP_CORS");
      setError(copy.zipErrors[zipError.code]);
      return false;
    } finally {
      setZipState("idle");
    }
  }

  return (
    <div className="tool-column">
      <div className="tool-card">
        <form onSubmit={submit}>
          <label className="sr-only" htmlFor="threads-url">{copy.inputLabel}</label>
          <div className="url-field">
            <Link2 size={20} aria-hidden="true" />
            <input id="threads-url" name="url" type="url" inputMode="url" maxLength={2048} required autoComplete="url" placeholder={copy.placeholder} value={url} onChange={(event) => setURL(event.target.value)} />
          </div>
          <button className="resolve-button" type="submit" disabled={loading}>
            {loading ? <RefreshCw className="spin" size={20} /> : <ArrowDownToLine size={20} />}
            <span>{loading ? copy.resolving : copy.resolve}</span>
          </button>
        </form>
        {error && <div className="inline-error" role="alert"><span>{error}</span>{mismatch && <a href={platformDownloaderPath(locale, mismatch)}>{platformLabel(mismatch)} Downloader</a>}{desktopHint && <a href={localizedPath(grabSiloDesktopLocale(locale), "desktop")}>{threadsLoginRequired[locale].cta}</a>}{requestID && <small>ID: {requestID}</small>}</div>}
      </div>
      <p className="mobile-value-line">{copy.heroLead}</p>

      {result && <ResultCard key={result.requestId} locale={locale} copy={copy} result={result} retry={() => submit()} createZip={createZip} zipState={zipState} onDownloadError={() => { setRequestID(undefined); setError(copy.downloadError); }} />}
      <DesktopPromoStrip locale={locale} />
    </div>
  );
}

interface ResultCardProps {
  locale: Locale;
  copy: CopyType;
  result: ResolveResponse;
  retry: () => void;
  createZip: (items: MediaItem[], filename: string) => Promise<boolean>;
  zipState: "idle" | "loading";
  onDownloadError: () => void;
}

type ThreadsContentType = "post" | "profile" | "reply";

function ResultCard(props: ResultCardProps) {
  if (props.result.contentType === "profile" && props.result.profile) {
    return <ProfileResultCard {...props} />;
  }
  return <PostResultCard {...props} />;
}

function PostResultCard({ locale, copy, result, retry, createZip, zipState, onDownloadError }: ResultCardProps) {
  const [copied, setCopied] = useState(false);
  const [htmlState, setHtmlState] = useState<"idle" | "loading">("idle");
  const [feedbackState, setFeedbackState] = useState<"idle" | "prompt" | "submitted">("idle");
  const [lastSaveFormat, setLastSaveFormat] = useState("unknown");
  const textLabels = { mainPost: copy.mainPostLabel, replies: copy.repliesTitle, quotedPost: copy.quoteTitle, partialReplies: copy.repliesPartial };

  function saveStarted(format: string, params: Record<string, string | number | boolean> = {}) {
    track("save_started", { platform: "threads", content_type: "post", format, ...params });
  }

  function saveCompleted(format: string, params: Record<string, string | number | boolean> = {}) {
    track("save_completed", { platform: "threads", content_type: "post", format, ...params });
    setLastSaveFormat(format);
    setFeedbackState((current) => current === "idle" ? "prompt" : current);
  }

  function saveFailed(format: string, params: Record<string, string | number | boolean> = {}) {
    track("save_failed", { platform: "threads", content_type: "post", format, ...params });
  }

  function saveFile(format: string, action: () => void, params: Record<string, string | number | boolean> = {}) {
    saveStarted(format, params);
    try {
      action();
      saveCompleted(format, params);
    } catch {
      saveFailed(format, params);
    }
  }

  async function copyText() {
    saveStarted("clipboard");
    try {
      await navigator.clipboard.writeText(buildThreadText(result, textLabels));
      setCopied(true);
      track("copy_text", { platform: "threads", content_type: "post" });
      saveCompleted("clipboard", { reply_count: result.replies.length });
      setTimeout(() => setCopied(false), 1600);
    } catch {
      saveFailed("clipboard");
    }
  }
  function saveText() {
    saveFile("txt", () => {
      const body = buildThreadText(result, textLabels);
      saveBlob(new Blob(["\uFEFF", body], { type: "text/plain;charset=utf-8" }), `threads_${result.author.username}_${result.source.postId}.txt`);
    }, { reply_count: result.replies.length });
  }
  function saveJson() {
    saveFile("json", () => saveBlob(new Blob([JSON.stringify(result, null, 2)], { type: "application/json;charset=utf-8" }), `threads_${result.author.username}_${result.source.postId}.json`), { reply_count: result.replies.length });
  }
  function saveCsv() {
    saveFile("csv", () => {
      const body = buildThreadCsv(result);
      saveBlob(new Blob(["\uFEFF", body], { type: "text/csv;charset=utf-8" }), `threads_${result.author.username}_${result.source.postId}.csv`);
    }, { reply_count: result.replies.length });
  }
  async function saveHtml() {
    if (htmlState === "loading") return;
    setHtmlState("loading");
    saveStarted("html", { media_count: result.media.length, reply_count: result.replies.length });
    try {
      const exportResult = await embedAvatarImages(result);
      const body = buildThreadHtml(exportResult, {
        mainPost: copy.mainPostLabel,
        replies: copy.repliesTitle,
        quotedPost: copy.quoteTitle,
        repliesComplete: copy.repliesComplete,
        repliesPartial: copy.repliesPartial,
        media: copy.mediaLabel
      });
      saveBlob(new Blob(["\uFEFF", body], { type: "text/html;charset=utf-8" }), `threads_${result.author.username}_${result.source.postId}.html`);
      saveCompleted("html", { media_count: result.media.length, reply_count: result.replies.length });
    } catch {
      saveFailed("html", { media_count: result.media.length, reply_count: result.replies.length });
    } finally {
      setHtmlState("idle");
    }
  }

  async function saveZip() {
    saveStarted("zip", { media_count: result.media.length });
    if (await createZip(result.media, `threads_${result.author.username}_${result.source.postId}.zip`)) saveCompleted("zip", { media_count: result.media.length });
    else saveFailed("zip", { media_count: result.media.length });
  }

  function submitFeedback(helpful: boolean) {
    track("feedback_submitted", { platform: "threads", content_type: "post", helpful, after_format: lastSaveFormat });
    setFeedbackState("submitted");
  }

  const hasVideo = result.media.some((item) => item.type === "video");
  const postText = (result.text || result.replies.length > 0) && (
    <div className="post-text">
      {result.text && <p>{result.text}</p>}
      <div className="text-actions">
        <button type="button" onClick={copyText}>{copied ? <Check size={15} /> : <Copy size={15} />}{copied ? copy.copied : copy.copyText}</button>
        <button type="button" onClick={saveText}><FileText size={15} />{copy.saveText}</button>
        <button type="button" onClick={saveHtml} disabled={htmlState === "loading"}>{htmlState === "loading" ? <RefreshCw className="spin" size={15} /> : <FileCode2 size={15} />}{htmlState === "loading" ? copy.savingHtml : copy.saveHtml}</button>
        <button type="button" onClick={saveJson}><Braces size={15} />{copy.saveJson}</button>
        <button type="button" onClick={saveCsv}><Table2 size={15} />{copy.saveCsv}</button>
      </div>
    </div>
  );

  return (
    <article className="result-card" id="result-card" aria-live="polite" tabIndex={-1}>
      <div className="result-header">
        <div>
          <p className="result-kicker"><Check size={14} />{copy.resultTitle}</p>
          <div className="author-line">
            <AuthorAvatar author={result.author} />
            <div><strong>{result.author.displayName || `@${result.author.username}`}</strong><span>@{result.author.username}</span></div>
          </div>
        </div>
        <button className="icon-button" type="button" onClick={retry} aria-label={copy.retry} title={copy.retry}><RefreshCw size={18} /></button>
      </div>

      {!hasVideo && postText}

      {result.media.length === 0 ? (
        <div className="empty-media"><FileText size={26} /><div><h3>{copy.noMediaTitle}</h3><p>{copy.noMediaBody}</p></div></div>
      ) : (
        <>
          <div className="media-toolbar"><strong>{result.media.length} {copy.mediaLabel}</strong>{result.media.length > 1 && <button type="button" className="secondary-button" onClick={saveZip} disabled={zipState === "loading"}>{zipState === "loading" ? <RefreshCw className="spin" size={16} /> : <Archive size={16} />}{zipState === "loading" ? copy.buildingZip : copy.downloadZip}</button>}</div>
          <div className="media-grid">{result.media.map((item) => <MediaCard key={item.id} item={item} contentType="post" copy={copy} onDownloadError={onDownloadError} onSaveComplete={saveCompleted} />)}</div>
          <p className="direct-note"><ExternalLink size={14} />{copy.directNote}</p>
        </>
      )}

      {hasVideo && postText}

      {result.quotedPost && <div className="quoted-card"><div className="quote-label"><Quote size={15} />{copy.quoteTitle}</div><strong>@{result.quotedPost.author.username}</strong><p>{result.quotedPost.text}</p>{result.quotedPost.media.length > 0 && <span>{result.quotedPost.media.length} {copy.mediaLabel}</span>}</div>}
      {(result.replies.length > 0 || result.replySummary.expectedDirectCount > 0) && <ReplyThread replies={result.replies} summary={result.replySummary} copy={copy} onDownloadError={onDownloadError} onSaveComplete={saveCompleted} />}
      {feedbackState !== "idle" && (
        <aside className="save-feedback" aria-live="polite">
          {feedbackState === "prompt" ? (
            <>
              <strong>{copy.feedbackQuestion}</strong>
              <div className="feedback-actions">
                <button type="button" onClick={() => submitFeedback(true)}><ThumbsUp size={15} />{copy.feedbackYes}</button>
                <button type="button" onClick={() => submitFeedback(false)}><ThumbsDown size={15} />{copy.feedbackNo}</button>
              </div>
              <small>{copy.feedbackPrivacy}</small>
            </>
          ) : <strong className="feedback-thanks"><Check size={16} />{copy.feedbackThanks}</strong>}
        </aside>
      )}
      <a className="source-link" href={result.source.canonicalUrl} target="_blank" rel="noreferrer" onClick={() => track("open_original", { platform: "threads", content_type: "post", target: "source_post" })}>Threads <ExternalLink size={13} /></a>
    </article>
  );
}

function ProfileResultCard({ copy, result, retry, createZip, zipState, onDownloadError }: ResultCardProps) {
  const profile = result.profile!;
  const [copied, setCopied] = useState(false);
  const [htmlState, setHtmlState] = useState<"idle" | "loading">("idle");
  const profileMedia = useMemo(() => profile.posts.flatMap((post) => post.media), [profile.posts]);
  const zipBatches = useMemo(() => mediaZipBatches(profileMedia), [profileMedia]);
  const safeUsername = result.author.username.replace(/[^A-Za-z0-9._-]+/g, "-") || "threads";
  const prefix = `threads_profile_${safeUsername}`;
  const textLabels = {
    profile: copy.profileLabel,
    biography: copy.profileBioLabel,
    posts: copy.profilePostsTitle,
    complete: copy.profileComplete,
    partial: copy.profilePartial,
    quotedPost: copy.quoteTitle
  };

  function saveCompleted(format: string, params: Record<string, string | number | boolean> = {}) {
    track("save_completed", { platform: "threads", content_type: "profile", format, ...params });
  }

  async function copyProfileText() {
    track("save_started", { platform: "threads", content_type: "profile", format: "clipboard" });
    try {
      await navigator.clipboard.writeText(buildProfileText(result, textLabels));
      setCopied(true);
      saveCompleted("clipboard", { post_count: profile.posts.length });
      setTimeout(() => setCopied(false), 1600);
    } catch {
      track("save_failed", { platform: "threads", content_type: "profile", format: "clipboard" });
    }
  }

  function saveProfileFile(format: "txt" | "json" | "csv", body: string, type: string) {
    track("save_started", { platform: "threads", content_type: "profile", format });
    try {
      saveBlob(new Blob(["\uFEFF", body], { type }), `${prefix}.${format}`);
      saveCompleted(format, { post_count: profile.posts.length });
    } catch {
      track("save_failed", { platform: "threads", content_type: "profile", format });
    }
  }

  async function saveProfileHtml() {
    if (htmlState === "loading") return;
    setHtmlState("loading");
    track("save_started", { platform: "threads", content_type: "profile", format: "html" });
    try {
      const embedded = await embedAvatarImages(result);
      const body = buildProfileHtml(embedded, {
        profile: copy.profileLabel,
        biography: copy.profileBioLabel,
        posts: copy.profilePostsTitle,
        complete: copy.profileComplete,
        partial: copy.profilePartial,
        media: copy.mediaLabel,
        quotedPost: copy.quoteTitle
      });
      saveBlob(new Blob(["\uFEFF", body], { type: "text/html;charset=utf-8" }), `${prefix}.html`);
      saveCompleted("html", { post_count: profile.posts.length, media_count: profileMedia.length });
    } catch {
      track("save_failed", { platform: "threads", content_type: "profile", format: "html" });
    } finally {
      setHtmlState("idle");
    }
  }

  async function saveZipBatch(batch: MediaItem[], index: number) {
    const params = { media_count: batch.length, batch: index + 1, batch_count: zipBatches.length };
    track("save_started", { platform: "threads", content_type: "profile", format: "zip", ...params });
    const filename = `${prefix}_media_${String(index + 1).padStart(2, "0")}.zip`;
    if (await createZip(batch, filename)) saveCompleted("zip", params);
    else track("save_failed", { platform: "threads", content_type: "profile", format: "zip", ...params });
  }

  const status = (profile.summary.complete ? copy.profileComplete : copy.profilePartial)
    .replace("{count}", String(profile.summary.loadedCount));
  return (
    <article className="result-card profile-result" id="result-card" aria-live="polite" tabIndex={-1}>
      <div className="result-header">
        <div>
          <p className="result-kicker"><Check size={14} />{copy.profileResultTitle}</p>
          <div className="author-line">
            <AuthorAvatar author={result.author} />
            <div><strong>{result.author.displayName || `@${result.author.username}`}</strong><span>@{result.author.username}</span></div>
          </div>
          {profile.biography && <p className="profile-biography"><strong>{copy.profileBioLabel}</strong><span>{profile.biography}</span></p>}
        </div>
        <button className="icon-button" type="button" onClick={retry} aria-label={copy.retry} title={copy.retry}><RefreshCw size={18} /></button>
      </div>

      <section className="profile-summary" aria-label={copy.profileLabel}>
        <span className={profile.summary.complete ? "reply-status complete" : "reply-status partial"}>{status}</span>
        {!profile.summary.complete && <p>{copy.profilePartialNote}</p>}
      </section>

      <div className="post-text profile-export-panel">
        <div className="text-actions">
          <button type="button" onClick={copyProfileText}>{copied ? <Check size={15} /> : <Copy size={15} />}{copied ? copy.copied : copy.copyProfile}</button>
          <button type="button" onClick={() => saveProfileFile("txt", buildProfileText(result, textLabels), "text/plain;charset=utf-8")}><FileText size={15} />{copy.saveProfileText}</button>
          <button type="button" onClick={saveProfileHtml} disabled={htmlState === "loading"}>{htmlState === "loading" ? <RefreshCw className="spin" size={15} /> : <FileCode2 size={15} />}{htmlState === "loading" ? copy.savingHtml : copy.saveHtml}</button>
          <button type="button" onClick={() => saveProfileFile("json", JSON.stringify(result, null, 2), "application/json;charset=utf-8")}><Braces size={15} />{copy.saveJson}</button>
          <button type="button" onClick={() => saveProfileFile("csv", buildProfileCsv(result), "text/csv;charset=utf-8")}><Table2 size={15} />{copy.saveCsv}</button>
        </div>
      </div>

      {profileMedia.length > 0 && <>
        <div className="media-toolbar profile-media-toolbar">
          <strong>{profileMedia.length} {copy.mediaLabel}</strong>
          <div className="profile-zip-actions">
            {zipBatches.map((batch, index) => <button key={index} type="button" className="secondary-button" onClick={() => saveZipBatch(batch, index)} disabled={zipState === "loading"}>{zipState === "loading" ? <RefreshCw className="spin" size={16} /> : <Archive size={16} />}{copy.downloadZipBatch.replace("{index}", String(index + 1)).replace("{total}", String(zipBatches.length))}</button>)}
          </div>
        </div>
      </>}

      <section className="profile-posts" aria-labelledby="profile-posts-title">
        <div className="reply-heading"><div><Layers3 size={16} /><h3 id="profile-posts-title">{copy.profilePostsTitle}</h3></div></div>
        {profile.posts.length === 0 ? <div className="empty-media"><FileText size={26} /><div><h3>{copy.profileNoPosts}</h3></div></div> : (
          <div className="profile-post-list">
            {profile.posts.map((post, index) => <article className="profile-post-card" key={post.postId}>
              <header><span>{String(index + 1).padStart(2, "0")}</span>{post.publishedAt && <time dateTime={post.publishedAt}>{post.publishedAt}</time>}<a href={post.permalink} target="_blank" rel="noreferrer" aria-label={`Threads ${post.postId}`}><ExternalLink size={14} /></a></header>
              {post.text && <p>{post.text}</p>}
              {post.media.length > 0 && <div className="media-grid">{post.media.map((item) => <MediaCard key={`${post.postId}-${item.id}`} item={item} contentType="profile" copy={copy} onDownloadError={onDownloadError} onSaveComplete={saveCompleted} />)}</div>}
              {post.quotedPost && <div className="quoted-card"><div className="quote-label"><Quote size={15} />{copy.quoteTitle}</div><strong>@{post.quotedPost.author.username}</strong><p>{post.quotedPost.text}</p>{post.quotedPost.media.length > 0 && <span>{post.quotedPost.media.length} {copy.mediaLabel}</span>}</div>}
            </article>)}
          </div>
        )}
      </section>

      <a className="source-link" href={result.source.canonicalUrl} target="_blank" rel="noreferrer" onClick={() => track("open_original", { platform: "threads", content_type: "profile", target: "source_profile" })}>Threads <ExternalLink size={13} /></a>
    </article>
  );
}

function ReplyThread({ replies, summary, copy, onDownloadError, onSaveComplete }: { replies: Reply[]; summary: ResolveResponse["replySummary"]; copy: CopyType; onDownloadError: () => void; onSaveComplete: (format: string, params?: Record<string, string | number | boolean>) => void }) {
  const status = summary.complete
    ? copy.repliesComplete.replace("{count}", String(summary.loadedTotalCount))
    : copy.repliesPartial.replace("{loaded}", String(summary.loadedDirectCount)).replace("{expected}", String(summary.expectedDirectCount));

  return (
    <section className="reply-thread" aria-labelledby="reply-thread-title">
      <div className="reply-heading">
        <div><MessageCircle size={16} /><h3 id="reply-thread-title">{copy.repliesTitle}</h3></div>
        <span className={summary.complete ? "reply-status complete" : "reply-status partial"}>{status}</span>
        {!summary.complete && <p className="reply-explanation">{copy.repliesPartialNote}</p>}
        {summary.loadedTotalCount > summary.loadedDirectCount && <p className="reply-explanation">{copy.repliesNested.replace("{count}", String(summary.loadedTotalCount))}</p>}
      </div>
      <div className="reply-list">
        {replies.map((reply) => <ReplyCard key={reply.postId} reply={reply} copy={copy} onDownloadError={onDownloadError} onSaveComplete={onSaveComplete} />)}
      </div>
    </section>
  );
}

function ReplyCard({ reply, copy, onDownloadError, onSaveComplete }: { reply: Reply; copy: CopyType; onDownloadError: () => void; onSaveComplete: (format: string, params?: Record<string, string | number | boolean>) => void }) {
  const depth = Math.min(Math.max(reply.depth, 1), 5);
  return (
    <article className="reply-card" style={{ marginLeft: Math.min(depth - 1, 3) * 18 }}>
      <div className="reply-author">
        <AuthorAvatar author={reply.author} />
        <div><strong>{reply.author.displayName || `@${reply.author.username}`}</strong><span>@{reply.author.username}</span></div>
        <a href={reply.permalink} target="_blank" rel="noreferrer" aria-label={`@${reply.author.username} Threads`} onClick={() => track("open_original", { platform: "threads", content_type: "reply", target: "reply_post" })}><ExternalLink size={13} /></a>
      </div>
      {reply.text && <p>{reply.text}</p>}
      {reply.media.length > 0 && <><small>{reply.media.length} {copy.replyMediaLabel}</small><div className="reply-media-grid">{reply.media.map((item) => <MediaCard key={item.id} item={item} contentType="reply" copy={copy} onDownloadError={onDownloadError} onSaveComplete={onSaveComplete} />)}</div></>}
    </article>
  );
}

function AuthorAvatar({ author }: { author: Author }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [author.avatarUrl]);
  const label = (author.displayName?.trim() || author.username || "@").slice(0, 1).toLocaleUpperCase();

  if (!author.avatarUrl || failed) {
    return <span className="avatar-fallback" aria-hidden="true">{label}</span>;
  }
  return <img src={author.avatarUrl} referrerPolicy="no-referrer" alt="" onError={() => setFailed(true)} />;
}

function MediaCard({ item, contentType, copy, onDownloadError, onSaveComplete }: { item: MediaItem; contentType: ThreadsContentType; copy: CopyType; onDownloadError: () => void; onSaveComplete: (format: string, params?: Record<string, string | number | boolean>) => void }) {
  const [downloading, setDownloading] = useState(false);

  async function download() {
    if (downloading) return;
    setDownloading(true);
    track("save_started", { platform: "threads", content_type: contentType, format: "media", media_type: item.type });
    try {
      const blob = await fetchMediaForDownload(item);
      saveBlob(blob, item.filename);
      onSaveComplete("media", { content_type: contentType, media_type: item.type });
    } catch {
      track("save_failed", { platform: "threads", content_type: contentType, format: "media", media_type: item.type });
      onDownloadError();
    } finally {
      setDownloading(false);
    }
  }

  return (
    <figure className="media-card">
      <div className="media-preview">
        {item.type === "video" ? <video controls preload="metadata" poster={item.thumbnailUrl} src={item.url} /> : <img src={item.thumbnailUrl || item.url} alt="" loading="lazy" referrerPolicy="no-referrer" />}
        <span className="media-type">{item.type === "video" ? <Video size={14} /> : <Images size={14} />}{item.type}</span>
      </div>
      <figcaption>
        <span>{item.width && item.height ? `${item.width} × ${item.height}` : item.filename}</span>
        <div className="media-actions">
          <button type="button" onClick={download} disabled={downloading}>{downloading ? <RefreshCw className="spin" size={15} /> : <Download size={15} />}{copy.openOriginal}</button>
          <a href={item.url} target="_blank" rel="noreferrer" referrerPolicy="no-referrer" onClick={() => track("open_original", { platform: "threads", content_type: contentType, target: "media", media_type: item.type })}>{copy.openFile}<ExternalLink size={14} /></a>
        </div>
      </figcaption>
    </figure>
  );
}

function AdSlot({ enabled, label }: { enabled: boolean; label: string }) {
  if (!enabled) return null;
  return <aside className="ad-slot" aria-label={label}><span>{label}</span></aside>;
}

function LegalPage({ locale, page, copy }: { locale: Locale; page: Extract<PageKind, "privacy" | "terms" | "copyright" | "contact">; copy: CopyType }) {
  const content = legalPage(locale, page, copy);
  const showsSupport = page === "contact" || page === "copyright";
  return <main className="legal-main"><div className="legal-card"><p className="section-kicker">SiloSave / {content.title}</p><h1>{content.title}</h1>{content.paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}{showsSupport && <a className="support-email" href="mailto:support@silosave.com" aria-label="Email SiloSave support"><Mail size={17} />support@silosave.com</a>}<a href={localizedPath(locale, "home")}><ArrowRight size={16} />{copy.backHome}</a></div></main>;
}

function NotFound({ locale, copy }: { locale: Locale; copy: CopyType }) {
  return <main className="legal-main"><div className="legal-card"><p className="error-number">404</p><h1>{copy.notFound}</h1><a href={localizedPath(locale, "home")}><ArrowRight size={16} />{copy.backHome}</a></div></main>;
}

function Footer({ locale, copy, footerNote, tiktokEnabled = false, douyinEnabled = false, xEnabled = false, bilibiliEnabled = false }: { locale: Locale; copy: CopyType; footerNote?: string | null; tiktokEnabled?: boolean; douyinEnabled?: boolean; xEnabled?: boolean; bilibiliEnabled?: boolean }) {
  const youtubeIntentLinks = (footerYouTubeIntentLinks[locale] || []).filter((item) => completeLocalesForPage(item.page).includes(locale));
  const threadsCompatibility = isThreadsCompatibilityLocale(locale) ? getThreadsCompatibilityContent(locale) : null;
  const resolvedFooterNote = footerNote === null ? "" : footerNote || copy.footerNote;
  return (
    <footer>
      <div className="footer-about">
        <div className="footer-brand"><span className="brand-mark"><Archive size={17} /></span><strong>SiloSave</strong></div>
        {resolvedFooterNote && <p>{resolvedFooterNote}</p>}
      </div>
      <nav aria-label="Footer navigation">
        <a href={localizedPath(grabSiloDesktopLocale(locale), "desktop")}>{getGrabSiloDesktopCopy(locale).navLabel}</a>
        <a href={localizedPath(locale, "youtube-downloader")}>YouTube</a>
        {youtubeIntentLinks.map((item) => <a key={item.page} href={localizedPath(locale, item.page)}>{item.label}</a>)}
        <a href={localizedPath(locale, "instagram-downloader")}>Instagram</a>
        {locale === "en" && <><a href={localizedPath("en", "instagram-reel-downloader")}>Reels</a><a href={localizedPath("en", "instagram-photo-downloader")}>Instagram Photos</a></>}
        {tiktokEnabled && <a href={localizedPath(locale, "tiktok-downloader")}>TikTok</a>}
        <a href={localizedPath(locale, "facebook-downloader")}>Facebook</a>
        {locale === "en" && <a href={localizedPath("en", "facebook-reels-downloader")}>Facebook Reels</a>}
        <a href={localizedPath(locale, "downloader")}>Threads</a>
        {threadsCompatibility && <a href={localizedPath(locale, "threads-downloader-compatibility")}>{threadsCompatibility.navigationLabel}</a>}
        {xEnabled && <a href={localizedPath(locale, "x-downloader")}>X (Twitter)</a>}
        {locale === "en" && xEnabled && <a href={localizedPath("en", "twitter-gif-downloader")}>Twitter GIF</a>}
        {douyinEnabled && <a href={localizedPath(locale, "douyin-downloader")}>Douyin</a>}
        {bilibiliEnabled && <a href={localizedPath(locale, "bilibili-downloader")}>Bilibili</a>}
        <a href={localizedPath(locale, "guides")}>{guideNavLabels[locale]}</a>
        <a href={localizedPath(locale, "privacy")}>{copy.privacy}</a>
        <a href={localizedPath(locale, "terms")}>{copy.terms}</a>
        <a href={localizedPath(locale, "copyright")}>{copy.copyright}</a>
        <a href={localizedPath(locale, "contact")}>{copy.contact}</a>
      </nav>
    </footer>
  );
}
